import { PlanType } from '@/types'

/**
 * Tier limits configuration
 * Defines project and user limits for each pricing tier
 */
export interface TierLimits {
  maxProjects: number | null // null = unlimited
  maxUsersPerProject: number
  tokenExpiryDays: number | null // null = never expires
}

export const TIER_LIMITS: Record<PlanType, TierLimits> = {
  free: {
    maxProjects: 1,
    maxUsersPerProject: 2, // 1 client + 1 superadmin
    tokenExpiryDays: 90,
  },
  pro: {
    maxProjects: null, // unlimited, pay per active project
    maxUsersPerProject: 5, // 4 clients + 1 agency admin
    tokenExpiryDays: null, // never expires
  },
  agency: {
    maxProjects: null, // unlimited
    maxUsersPerProject: 20, // clients or admins
    tokenExpiryDays: null, // never expires
  },
}

/**
 * Tier features configuration
 * Defines which features are available for each pricing tier
 */
export interface TierFeatures {
  // SEO & Performance
  seoManual: boolean // Run SEO audits manually
  seoScheduled: boolean // Schedule automatic SEO audits
  performanceManual: boolean // Run performance checks manually
  performanceScheduled: boolean // Schedule automatic performance checks

  // Monitoring
  uptimeMonitoring: boolean // Uptime monitoring available
  uptimeInstantNotifications: boolean // Instant notifications (false = 1 hour delayed)
  domainMonitoring: boolean // Domain/SSL expiry monitoring

  // Assets & Content
  imageHosting: boolean // CDN image hosting
  tagManager: boolean // Simple tag manager

  // Bulk Operations
  bulkExport: boolean // Export work items in bulk
  bulkImport: boolean // Import results from AI agents

  // Agency Features
  brandedReports: boolean // Branded monthly reports
  hourPackages: boolean // Hour package & time tracker
  quotes: boolean // Make quotes & approval workflow
  whiteLabel: boolean // White label branding (coming soon)

  // Changelog
  changelog: boolean // Project changelog feature

  // Credentials
  secureCredentials: boolean // Securely share/store credentials
}

export const TIER_FEATURES: Record<PlanType, TierFeatures> = {
  free: {
    // SEO & Performance - all available
    seoManual: true,
    seoScheduled: true,
    performanceManual: true,
    performanceScheduled: true,

    // Monitoring - all available
    uptimeMonitoring: true,
    uptimeInstantNotifications: true,
    domainMonitoring: true,

    // Assets & Content
    imageHosting: true,
    tagManager: true,

    // Bulk Operations - available (pay per use with tokens)
    bulkExport: true,
    bulkImport: true,

    // All features available on free tier (only limited on projects/users)
    brandedReports: true,
    hourPackages: true,
    quotes: true,
    whiteLabel: false, // coming soon

    // Other
    changelog: true,
    secureCredentials: true,
  },
  pro: {
    // SEO & Performance - all available
    seoManual: true,
    seoScheduled: true,
    performanceManual: true,
    performanceScheduled: true,

    // Monitoring - all available
    uptimeMonitoring: true,
    uptimeInstantNotifications: true,
    domainMonitoring: true,

    // Assets & Content
    imageHosting: true,
    tagManager: true,

    // Bulk Operations - available
    bulkExport: true,
    bulkImport: true,

    // All features available
    brandedReports: true,
    hourPackages: true,
    quotes: true,
    whiteLabel: false, // coming soon

    // Other
    changelog: true,
    secureCredentials: true,
  },
  agency: {
    // SEO & Performance - all available
    seoManual: true,
    seoScheduled: true,
    performanceManual: true,
    performanceScheduled: true,

    // Monitoring - all available
    uptimeMonitoring: true,
    uptimeInstantNotifications: true,
    domainMonitoring: true,

    // Assets & Content
    imageHosting: true,
    tagManager: true,

    // Bulk Operations - available
    bulkExport: true,
    bulkImport: true,

    // Agency Features - all available
    brandedReports: true,
    hourPackages: true,
    quotes: true,
    whiteLabel: false, // coming soon

    // Other
    changelog: true,
    secureCredentials: true,
  },
}

/**
 * Token pricing tiers
 */
export interface TokenBundle {
  tokens: number
  priceEuroCents: number
  pricePerToken: number // calculated
}

export const TOKEN_BUNDLES: TokenBundle[] = [
  { tokens: 100, priceEuroCents: 1000, pricePerToken: 0.10 },
  { tokens: 500, priceEuroCents: 4500, pricePerToken: 0.09 },
  { tokens: 1000, priceEuroCents: 9000, pricePerToken: 0.09 },
]

/**
 * Get tier limits for a plan type
 */
export function getPlanLimits(planType: PlanType): TierLimits {
  return TIER_LIMITS[planType] ?? TIER_LIMITS.free
}

/**
 * Get tier features for a plan type
 */
export function getPlanFeatures(planType: PlanType): TierFeatures {
  return TIER_FEATURES[planType] ?? TIER_FEATURES.free
}

/**
 * Check if a specific feature is available for a plan type
 */
export function hasFeature(planType: PlanType, feature: keyof TierFeatures): boolean {
  const features = getPlanFeatures(planType)
  return features[feature] ?? false
}

/**
 * Get display name for a plan type
 */
export function getPlanDisplayName(planType: PlanType): string {
  switch (planType) {
    case 'free':
      return 'Start'
    case 'pro':
      return 'Pro'
    case 'agency':
      return 'Agency'
    default:
      return 'Free'
  }
}

/**
 * Get the next tier upgrade from current tier
 */
export function getNextTier(planType: PlanType): PlanType | null {
  switch (planType) {
    case 'free':
      return 'pro'
    case 'pro':
      return 'agency'
    case 'agency':
      return null // already at highest tier
    default:
      return 'pro'
  }
}

/**
 * Check if a tier is higher than another
 */
export function isHigherTier(tier1: PlanType, tier2: PlanType): boolean {
  const tierOrder: Record<PlanType, number> = {
    free: 0,
    pro: 1,
    agency: 2,
  }
  return tierOrder[tier1] > tierOrder[tier2]
}

/**
 * Free tier signup bonus tokens
 */
export const FREE_TIER_SIGNUP_TOKENS = 100
